import { Box } from '@mui/material';
import { GLOBAL_ROI } from '@sofarocean/atlas/constants';
import AnalyticsContext, { AnalyticsContextDefaults } from '@sofarocean/atlas/contexts/AnalyticsContext';
import { TimelineContextProvider } from '@sofarocean/atlas/contexts/TimelineContext';
import WeatherContext, { useWeatherState } from '@sofarocean/atlas/contexts/WeatherContext';
import { PropsWithChildren, useEffect } from 'react';

import { TopSpotList } from '@/components/map/TopSpotList';
import { TopSpotMap } from '@/components/map/TopSpotMap';
import { MapUiContextProvider, useMapUiContext } from '@/context/MapUiContext';
import { TopSpotTourContextProvider } from '@/context/TopSpotTourContext';
import { useFeatureFlag } from '@/hooks/useFeatureFlag';

export const TopSpot = () => {
    useEffect(() => {
        // stop trackpad pinching from scaling the page
        const onWheel = (e: WheelEvent) => {
            if (e.ctrlKey) {
                // only set on pinches, not scrolls
                e.preventDefault();
            }
        };

        document.addEventListener('wheel', onWheel, { passive: false });
        return () => document.removeEventListener('wheel', onWheel);
    }, []);

    return (
        <AnalyticsContext.Provider value={AnalyticsContextDefaults}>
            <TimelineContextProvider>
                <MapUiContextProvider>
                    <MaybeWeatherProvider>
                        <TopSpotTourContextProvider>
                            <Box sx={{ height: 1, overflow: 'clip', position: 'relative' }}>
                                <TopSpotMap />
                                <TopSpotList />
                            </Box>
                        </TopSpotTourContextProvider>
                    </MaybeWeatherProvider>
                </MapUiContextProvider>
            </TimelineContextProvider>
        </AnalyticsContext.Provider>
    );
};

export const MaybeWeatherProvider = ({ children }: PropsWithChildren) => {
    const weatherMapFlag = useFeatureFlag('weatherMap');

    if (weatherMapFlag) {
        return <WeatherProvider>{children}</WeatherProvider>;
    }
    return <>{children}</>;
};

const WeatherProvider = ({ children }: PropsWithChildren) => {
    const { setWeatherModel } = useMapUiContext();
    const weatherValue = useWeatherState({
        trackAnalyticsEvent: () => {},
        localWeatherWorker: true,
    });

    useEffect(() => {
        setWeatherModel('sofar');
    }, [setWeatherModel]);

    useEffect(() => {
        if (weatherValue.currentForecastMetadata.downloadStatus !== 'idle') {
            return;
        }
        try {
            weatherValue.setRegionOfInterest(GLOBAL_ROI);
        } catch (e) {
            console.warn('Exception setting weather region of interest', e);
        }
    }, [weatherValue, weatherValue.currentForecastMetadata.downloadStatus]);

    return <WeatherContext.Provider value={weatherValue}>{children}</WeatherContext.Provider>;
};
